import { CollapsibleFilters } from '../common/CollapsibleFilters';
// ContactList.tsx - Contact Management with Lead Scoring and Qualification
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Modal,
  ModalDialog,
  Input,
  IconButton,
  Select,
  Option,
  FormLabel,
} from '@mui/joy';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import { Plus, User, Envelope as Mail, Phone, Buildings as Building, MagnifyingGlass as Search, X, UploadSimple as Upload } from '@phosphor-icons/react';
import { SimpleContactModal } from '../global/SimpleContactModal';
import { ContactForm } from './contacts/ContactForm';
import { TagPicker } from '../tags/TagPicker';
import { useContacts } from '../../hooks/useCrm';
import { useTags } from '../../hooks/useTags';
import { useDebounce } from '../../hooks/useDebounce';
import type { ContactListItem } from '../../types/crm';

// Local alias so the rest of this file (and the ResponsiveTable column type
// parameter) reads naturally; matches the shape the API actually returns.
type Contact = ContactListItem;

const ContactList: React.FC = () => {
  const navigate = useNavigate();
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [ordering, setOrdering] = useState<string>('-created_at');
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: availableTags = [] } = useTags();

  // Reset to page 1 when search/filter/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, selectedTagIds, ordering]);

  // Use React Query hook to fetch contacts with search filter and pagination
  const { data, isLoading: loading, error, refetch: fetchContacts } = useContacts({
    search: debouncedSearchQuery || undefined,
    tags: selectedTagIds.length ? selectedTagIds : undefined,
    ordering: ordering || undefined,
    page: currentPage,
  });
  const contacts = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getContactTypeColor = (type: string) => {
    switch (type) {
      case 'customer': return 'success';
      case 'prospect': return 'warning';
      case 'lead': return 'primary';
      default: return 'neutral';
    }
  };

  const getQualificationColor = (status: string) => {
    switch (status) {
      case 'qualified': return 'success';
      case 'unqualified': return 'neutral';
      case 'disqualified': return 'danger';
      default: return 'neutral';
    }
  };

  // Lead score color function - no longer needed as we removed the lead score column
  // const getLeadScoreColor = (score: number) => {
  //   if (score >= 70) return 'success';
  //   if (score >= 40) return 'warning';
  //   return 'danger';
  // };

  const handleViewContact = (contactId: number) => {
    navigate(`/crm/contacts/${contactId}`);
  };

  const handleAddContact = () => {
    setShowAddContactModal(true);
  };

  const handleContactCreated = (newContact: any) => {
    // Simply close the modal - React Query will handle the list refresh
    setShowAddContactModal(false);
  };

  const handleEditContact = (contactId: number) => {
    setEditingContactId(contactId);
    setShowEditContactModal(true);
  };

  const handleContactUpdated = () => {
    // React Query will handle the list refresh automatically
    setShowEditContactModal(false);
    setEditingContactId(null);
  };

  const contactColumns: ResponsiveColumn<Contact>[] = [
    {
      key: 'contact',
      sortValue: (row) => row.full_name,
      header: 'Contact',
      width: '200px',
      mobileVariant: 'header',
      render: (contact) => (
        <Box sx={{ minWidth: 0 }}>
          <Link
            component={RouterLink}
            to={`/crm/contacts/${contact.id}`}
            level="title-sm"
            sx={{
              fontWeight: 'bold',
              cursor: 'pointer',
              color: 'var(--brand-text, #7013f5)',
              '&:hover': { textDecoration: 'underline' },
              wordBreak: 'break-word',
            }}
          >
            {contact.full_name}
          </Link>
          {contact.company_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Building size={12} />
              <Typography level="body-xs" color="neutral">
                {contact.company_name}
              </Typography>
            </Box>
          )}
          {contact.primary_address && (
            <Typography level="body-xs" color="neutral">
              {contact.primary_address}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'tags',
      sortValue: (row) => row.tags?.length ?? 0,
      header: 'Tags',
      width: '130px',
      render: (contact) =>
        contact.tags && contact.tags.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {contact.tags.map((tag) => (
              <Chip
                key={tag.id}
                size="sm"
                variant="solid"
                sx={{
                  backgroundColor: tag.color,
                  color: '#fff',
                  fontSize: '10px',
                  height: '18px',
                  minHeight: '18px',
                  paddingX: '6px',
                }}
              >
                {tag.name}
              </Chip>
            ))}
          </Box>
        ) : (
          <Typography level="body-xs" color="neutral">—</Typography>
        ),
    },
    {
      key: 'contact_info',
      sortValue: (row) => row.email,
      header: 'Contact Info',
      width: '250px',
      render: (contact) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Mail size={12} style={{ flexShrink: 0 }} />
              <Typography level="body-xs" sx={{ minWidth: 0, overflowWrap: 'anywhere', textAlign: 'start' }}>
                {contact.email}
              </Typography>
            </Box>
          )}
          {contact.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone size={12} />
              <Typography level="body-xs">{contact.phone}</Typography>
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'type',
      sortValue: (row) => row.contact_type,
      header: 'Type',
      width: '140px',
      render: (contact) => (
        <Chip variant="soft" color={getContactTypeColor(contact.contact_type)} size="sm">
          {contact.contact_type}
        </Chip>
      ),
    },
    {
      key: 'assigned_to',
      sortValue: (row) => row.assigned_to_name,
      header: 'Assigned To',
      width: '150px',
      render: (contact) => (
        <Typography level="body-sm">{contact.assigned_to_name || 'Unassigned'}</Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">
          <Typography level="body-sm">Error loading contacts: {error instanceof Error ? error.message : 'An error occurred'}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'flex-end',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
        }}
      >
        {/* Page title comes from the Sales shell (Sales: Contacts) */}
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Upload size={18} />}
            onClick={() => navigate('/settings/imports')}
            sx={{ flex: { xs: 1, md: 'none' } }}
          >
            Import
          </Button>
          <Button
            variant="solid"
            color="primary"
            startDecorator={<Plus size={18} />}
            onClick={handleAddContact}
            sx={{ flex: { xs: 1, md: 'none' } }}
          >
            New Contact
          </Button>
        </Box>
      </Box>

      {/* Search / Filter / Sort */}
      <CollapsibleFilters><Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-end' },
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: { md: 500 } }}>
          <FormLabel sx={{ mb: 0.5 }}>Search</FormLabel>
          <Input
            placeholder="Search contacts by name, email, phone, company, or tag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startDecorator={<Search size={18} />}
            endDecorator={
              searchQuery && (
                <IconButton
                  variant="plain"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={18} />
                </IconButton>
              )
            }
            sx={{ width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: { md: 360 } }}>
          <TagPicker
            label="Filter by tags"
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            availableTags={availableTags}
            placeholder="Filter by tags..."
          />
        </Box>
        <Box sx={{ width: { xs: '100%', md: 220 } }}>
          <FormLabel sx={{ mb: 0.5 }}>Sort by</FormLabel>
          <Select
            value={ordering}
            onChange={(_e, value) => value && setOrdering(value)}
          >
            <Option value="-created_at">Newest</Option>
            <Option value="created_at">Oldest</Option>
            <Option value="last_name">Name (A–Z)</Option>
            <Option value="-last_name">Name (Z–A)</Option>
            <Option value="-last_contacted">Last contacted</Option>
            {/* No tag sort: ordering on tags__name joins the tag table, so a
                multi-tag contact comes back once per tag while `count` stays
                de-duplicated — duplicate rows and contacts pushed off the last
                page. */}
          </Select>
        </Box>
      </Box></CollapsibleFilters>

      {contacts.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <User size={48} color="var(--joy-palette-neutral-400)" style={{ margin: '0 auto 16px' }} />
            <Typography level="h4" sx={{ mb: 1 }}>
              {searchQuery ? 'No matching contacts' : 'No contacts found'}
            </Typography>
            <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
              {searchQuery
                ? `No contacts match "${searchQuery}". Try adjusting your search.`
                : 'Start building your customer database by adding your first contact'}
            </Typography>
            {!searchQuery && (
              <Button variant="solid" color="primary" startDecorator={<Plus size={18} />} onClick={handleAddContact}>
                Add First Contact
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <ResponsiveTable<Contact>
          rows={contacts}
          getRowKey={(c) => c.id}
          columns={contactColumns}
          rowActions={(contact) => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                onClick={() => handleViewContact(contact.id)}
              >
                View
              </Button>
              <Button
                variant="plain"
                size="sm"
                onClick={() => handleEditContact(contact.id)}
              >
                Edit
              </Button>
            </Box>
          )}
          actionsWidth="120px"
        />
      )}

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
        }}
      >
        <Typography level="body-sm" color="neutral">
          {totalCount > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} contacts`
            : 'No contacts'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Add Contact Modal */}
      <SimpleContactModal
        open={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onSuccess={handleContactCreated}
      />

      {/* Edit Contact Modal */}
      <Modal open={showEditContactModal} onClose={() => setShowEditContactModal(false)}>
        <ModalDialog size="lg" sx={{ maxWidth: '1000px', width: '94vw', maxHeight: '92vh', overflow: 'hidden', p: 0 }}>
          {editingContactId && (
            <ContactForm
              contactId={editingContactId}
              onSave={handleContactUpdated}
              onCancel={() => setShowEditContactModal(false)}
            />
          )}
        </ModalDialog>
      </Modal>

    </Box>
  );
};

export default ContactList;
